import React from "react";
import { COACH_PROGRAM_CURRICULUM_ROUTE } from "../../../routes/routes";
import { FilePlusIcon } from "lucide-react";

import { Link } from "../../link/Link";
import { HeaderButton } from "../../button/HeaderButton";
import ProgramTemplatesService from "../../../services/ProgramTemplatesService";
import { COACH_PROGRAMS_LIST_QUERY_KEY } from "../CoachProgramsListScreen";
import { COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY } from "../../program-library/ProgramsTableScreen";
import { useNavigate } from "react-router-dom";
import { useToastAlert } from "../../app/ToastAlert/ToastAlertProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { CreateProgramFromTemplateDialog } from "../../dialog/CreateProgramFromTemplateDialog";
import { EXERCISE_KEY_TO_INVALIDATE_ON_USE_TEMPLATE } from "../../exercise-library/hooks/useArchiveRestoreLibraryExercise";

interface CreateProgramFromTemplateButtonProps {
  templateId: number;
  programName?: string;
}

export function CreateProgramFromTemplateButton({
  templateId,
  programName,
}: CreateProgramFromTemplateButtonProps) {
  const [createProgramDialogOpen, setCreateProgramDialogOpen] =
    React.useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToastAlert } = useToastAlert();
  const [trackEvent] = useAnalytics();

  const handleDialogOpen = React.useCallback(() => {
    setCreateProgramDialogOpen(true);
  }, []);
  const handleDialogClose = React.useCallback(() => {
    setCreateProgramDialogOpen(false);
  }, []);

  const { mutate: createProgram, isPending: isCreatingProgram } = useMutation({
    mutationFn: ProgramTemplatesService.useProgramTemplate,
    onSuccess: (slug) => {
      queryClient.invalidateQueries({
        queryKey: [COACH_PROGRAMS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY],
      });

      EXERCISE_KEY_TO_INVALIDATE_ON_USE_TEMPLATE.forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey: [queryKey],
          exact: false,
        });
      });
      showToastAlert("success", {
        message: "Successfully created new program using template.",
      });
      navigate(COACH_PROGRAM_CURRICULUM_ROUTE.replace(":slug", slug));
    },
  });

  const handleDialogConfirm = React.useCallback(
    (programName: string) => {
      if (templateId) {
        createProgram(
          { id: templateId, name: programName },
          {
            onSuccess: () => {
              setCreateProgramDialogOpen(false);
              trackEvent("Coach - Use Program Template", {
                name: programName,
                templateId: templateId,
              });
            },
          },
        );
      }
    },
    [templateId, createProgram, trackEvent],
  );

  return (
    <>
      <HeaderButton
        startIcon={<FilePlusIcon />}
        component={Link}
        children="Create a program"
        onClick={handleDialogOpen}
      />
      <CreateProgramFromTemplateDialog
        open={createProgramDialogOpen}
        onClose={handleDialogClose}
        templateName={programName}
        onConfirm={handleDialogConfirm}
        isPending={isCreatingProgram}
      />
    </>
  );
}
